import React from 'react'
import { useBarcode, useBarcodePosition } from '../hooks/useBarcode'
import { useBarcodeContext } from '../contexts/BarcodeContext'
import { Button } from './shadcn/Button'
import Modal from './Modal'
import { BarcodeEvent } from '../types/barcode'

interface BarcodeScannerDemoProps {
  isOpen: boolean
  onClose: () => void
}

export const BarcodeScannerDemo: React.FC<BarcodeScannerDemoProps> = ({ isOpen, onClose }) => {
  const { isScanning, scanner, updateConfig } = useBarcodeContext()
  const [lastScan, setLastScan] = React.useState<BarcodeEvent | null>(null)
  const [scanHistory, setScanHistory] = React.useState<BarcodeEvent[]>([])
  const clearHistory = () => {
    setLastScan(null)
    setScanHistory([])
  }

  // Example 1: Basic barcode scanning
  useBarcode(
    event => {
      console.log('Scanned:', event.data, 'Type:', event.type)
      setLastScan(event)
      setScanHistory(prev => [...prev, event])
    },
    { autoStart: true },
  )

  // Example 2: Position scanning
  useBarcodePosition(
    position => {
      console.log('Position scanned:', position)
    },
    { autoStart: true },
  )

  // Example 3: Filtered scanning (only specific barcode types)
  useBarcode(
    event => {
      console.log('Product barcode scanned:', event.data)
    },
    {
      autoStart: true,
      filter: event => event.type === 'BARCODE' && event.data.length > 10,
    },
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Barcode Scanner Test">
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span>Scanner Status:</span>
            <span className={`font-semibold ${isScanning ? 'text-green-600' : 'text-red-600'}`}>
              {isScanning ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Time to Scan:</span>
            <span className="font-semibold">{scanner?.getConfig()?.timeToScan ?? 0}ms</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Audio Feedback:</span>
            <span className="font-semibold">{scanner?.getConfig()?.enableAudio ? 'On' : 'Off'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Debug Mode:</span>
            <span className="font-semibold">{scanner?.getConfig()?.enableDebugMode ? 'On' : 'Off'}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => updateConfig({ enableAudio: !(scanner?.getConfig()?.enableAudio ?? false) })}
            variant="outline"
            size="sm">
            Toggle Audio
          </Button>
          <Button
            onClick={() => updateConfig({ enableDebugMode: !(scanner?.getConfig()?.enableDebugMode ?? false) })}
            variant="outline"
            size="sm">
            Toggle Debug
          </Button>
          <Button onClick={clearHistory} variant="outline" size="sm">
            Clear History
          </Button>
        </div>

        {lastScan && (
          <div className="p-3 bg-gray-100 rounded">
            <h3 className="font-semibold mb-1">Last Scan:</h3>
            <div className="text-sm space-y-1">
              <div>
                Type: <span className="font-mono">{lastScan.type}</span>
              </div>
              <div>
                Data: <span className="font-mono">{lastScan.data}</span>
              </div>
              <div>Time: {new Date(lastScan.timestamp).toLocaleTimeString()}</div>
              {lastScan.raw && lastScan.raw !== lastScan.data && (
                <div>
                  Raw: <span className="font-mono text-xs">{lastScan.raw}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <h3 className="font-semibold mb-1">Scan History:</h3>
          <div className="max-h-40 overflow-y-auto border rounded p-2 text-sm">
            {scanHistory.length === 0 ? (
              <div className="text-gray-500">No scans yet. Try scanning a barcode!</div>
            ) : (
              scanHistory
                .slice()
                .reverse()
                .map((scan, index) => (
                  <div key={index} className="font-mono text-xs py-0.5">
                    {new Date(scan.timestamp).toLocaleTimeString()} - {scan.type}: {scan.data}
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="text-xs text-gray-500">
          <p>Try scanning different types of barcodes:</p>
          <ul className="list-disc list-inside mt-1">
            <li>Product barcodes (EAN/Code)</li>
            <li>Position codes (XXX-YYY-ZZZ)</li>
            <li>GS1 codes</li>
            <li>QR codes with URLs</li>
          </ul>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  )
}
